import React, { useState, useEffect, useMemo } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useParams, NavLink } from 'react-router-dom';
import { DateTime } from 'luxon';
import { green } from '@material-ui/core/colors';

import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Divider from '@material-ui/core/Divider';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import FormControl from '@material-ui/core/FormControl';;
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Avatar from '@material-ui/core/Avatar';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';

import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import AssignmentIcon from '@material-ui/icons/Assignment';
import CommentIcon from '@material-ui/icons/Comment';
import CloseIcon from '@material-ui/icons/Close';

import EditRecommendationDialog from 'app/components/election/EditRecommendationDialog';
import Loading from 'app/components/loaders/Loading';

import useApi from 'app/context/useApi';
import useApp from 'app/context/useApp';

import { paper } from 'app/styles/core';
import { container } from 'app/styles/new-layout';

const useStyles = makeStyles(theme => ({
    container: {
        ...container(theme),
        padding: theme.spacing(3),
        minHeight: '80vh',
        position: 'relative'
    },
    paper: {
        ...paper(theme),
    },
    listItem: {
        borderRadius: theme.shape.borderRadius,
        backgroundColor: '#ffffff',
        boxShadow: theme.shadows[4],
        marginBottom: theme.spacing(3)
    },
    marginBottom: {
        marginBottom: theme.spacing(3)
    },
    marginBottomSmall: {
        marginBottom: theme.spacing(1)
    },
    tabText: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3)
    },
    empty: {
        paddingTop: theme.spacing(5),
        paddingBottom: theme.spacing(5),
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3)
    },
    searchInput: {
        minWidth: 280
    }
}));

export default function ViewRecommendations(props) {

    let params = useParams();
    const classes = useStyles();
    const api = useApi();
    const { app } = useApp();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [ election, setElection ] = useState({});
    const [ sortedProposals, setSortedProposals ] = useState([]);
    const [ relatedProposals, setRelatedProposals ] = useState([]);
    const [ tab, setTab ] = useState('all');
    const [ searchValue, setSearchValue ] = useState("");

    const [ activeProposal, setActiveProposal ] = useState(null);
    const [ editRecommendationDialogOpen, setEditRecommendationDialogOpen ] = useState(false);

    const [ loading, setLoading ] = useState(false);

    // Getters ------------------------------------------

    const getElection = () => {
        setLoading(true);

        api.get(`/election/${params.id}/recommendation`)
            .then(res => {
                setElection(res.election);
                setSortedProposals(res.election.sortedProposals);
                setRelatedProposals(res.election.relatedProposals);
            }).finally(() => setLoading(false));
    }

    useEffect(() => {
        getElection();
    }, []);

    // Handlers -----------------------------------------

    const handleTab = (event, newTab) => {
        setTab(newTab);
    };

    const handleSearchValue = event => {
        setSearchValue(event.target.value);
    };

    const handleSearch = (proposals) => {

        return proposals.filter((p) => {
            let match = false;
            if (p.title !== null) match = p.title.toLowerCase().includes(searchValue.toLowerCase());
            return match;
        })

    };

    const handleClearSearch = () => {
        setSearchValue("");
    }

    // Dialog Functions

    const openEditRecommendationDialog = (proposal) => {
        setActiveProposal(proposal);
        setEditRecommendationDialogOpen(true);
    }

    const closeEditRecommendationDialog = () => {
        setEditRecommendationDialogOpen(false);
    }

    const closeEditRecommendationDialogAndRefresh = () => {
        closeEditRecommendationDialog();
        getElection();
    }

    const editRecommendationDialogClosed = () => {
        setActiveProposal(null);
    }

    // Constants

    const recommendedProposals = useMemo(() => {
        return sortedProposals.filter(p => p.recommendations.length > 0);
    }, [sortedProposals]);

    const proposals = useMemo(() => {
        console.log(sortedProposals, recommendedProposals, relatedProposals)
        switch (tab) {
            case 'all':
                return (searchValue.length > 0) ? handleSearch(sortedProposals) : sortedProposals;
                break;
            case 'recommended':
                return (searchValue.length > 0) ? handleSearch(recommendedProposals) : recommendedProposals;
                break;
            case 'related':
                return (searchValue.length > 0) ? handleSearch(relatedProposals) : relatedProposals;
                break;
        }
    }, [election, sortedProposals, recommendedProposals, relatedProposals, tab, searchValue]);

    const getTabText = () => {
        switch (tab) {
            case 'all':
                return `Viewing all ${election.button_name} proposals. Write Recommendations for proposals by clicking on the proposal you want to recommend.`
            case 'recommended':
                return `Viewing ${election.button_name} proposals you have previously written recommendations for. To edit Recommendations, click on the proposal you want to change.`
            case 'related':
                return `Viewing proposals that have tagged you as related to the ${election.button_name}. Write Recommendations for related proposals by clicking on the proposal you want to recommend.`
            default: return '';
        }
    }

    return (
        <div className={classes.container}>
            <Loading isLoading={loading}/>
            {election &&
                <>
                    <Grid
                        container
                        spacing={1}
                        justify="space-between"
                        alignItems="center"
                        className={classes.marginBottom}
                        >
                        <Grid item>
                            <Typography
                                color="secondary"
                                variant="h4"
                                component="h2"
                                gutterBottom={isMobile}
                                >
                                {election.formatted_name}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={'auto'}>
                            <FormControl
                                variant="outlined"
                                fullWidth
                                className={classes.searchInput}
                                >
                                <OutlinedInput
                                    id="input-search"
                                    value={searchValue}
                                    onChange={handleSearchValue}
                                    placeholder="Search Proposals..."
                                    color="secondary"
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="Clear Search Field"
                                                onClick={handleClearSearch}
                                                edge="end"
                                                >
                                                <CloseIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                    <Tabs
                        value={tab}
                        onChange={handleTab}
                        indicatorColor="secondary"
                        textColor="secondary"
                        centered
                        >
                        <Tab label={isMobile ? 'All' : election.recommendations_all_label ?? 'All Proposals'} value="all"/>
                        <Tab label={isMobile ? "Saved" : election.recommendations_submitted_label ?? "Submitted Proposals"} value="recommended"/>
                        <Tab label={(isMobile ? "Related" : election.recommendations_related_label ?? "Related Proposals") + ` (${relatedProposals.length})`} value="related"/>
                    </Tabs>
                    <Divider/>
                    <div>
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            className={classes.tabText}
                            >
                            {getTabText()}
                        </Typography>
                        {(proposals && proposals.length > 0) ? (
                            <List>
                                {proposals.map(p => (
                                    <ListItem
                                        key={p.id}
                                        button
                                        onClick={() => openEditRecommendationDialog(p)}
                                        className={classes.listItem}
                                        >
                                        <ListItemAvatar>
                                            <Avatar>
                                                <AssignmentIcon />
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={p.title}
                                            secondaryTypographyProps={{ component: "div"}}
                                            secondary={
                                                <>
                                                    {(p.subtitle && p.subtitle != '') &&
                                                        <Typography
                                                            variant="subtitle2"
                                                            color="textPrimary"
                                                            >
                                                            {p.subtitle}
                                                        </Typography>
                                                    }
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                        >
                                                        Proposed On: <strong>{DateTime.fromISO(p.created_at).toLocaleString(DateTime.DATE_MED)}</strong><br/>
                                                        Sponsors: <strong>{p.sponsors}</strong>
                                                    </Typography>
                                                </>
                                            }
                                        />
                                        <ListItemSecondaryAction>
                                            <IconButton onClick={() => openEditRecommendationDialog(p)}>
                                                {p.recommendations.length ? (
                                                    <CheckCircleOutlineIcon style={{ color: green[600] }}/>
                                                ) : (
                                                    <CommentIcon color="action"/>
                                                )}
                                            </IconButton>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Typography
                                align="center"
                                color="textSecondary"
                                variant="subtitle2"
                                className={classes.empty}
                                >
                                No proposals could be found. Please try again later.<br/>
                            </Typography>
                        )}
                    </div>
                    <EditRecommendationDialog
                        election={election}
                        proposal={activeProposal}
                        recommendation={activeProposal?.recommendations[0]}
                        open={editRecommendationDialogOpen}
                        onClose={closeEditRecommendationDialog}
                        onSave={closeEditRecommendationDialogAndRefresh}
                        onDelete={closeEditRecommendationDialogAndRefresh}
                        onClosed={editRecommendationDialogClosed}
                    />
                </>
            }
        </div>
    );
}
